import { getZenApiWsBaseUrl } from '@/config/zen-api';
import { getActiveTenantId } from '@/utils/active-tenant';
import { REPLY_FAILED_ERROR } from '@/utils/conversation-reconciliation';
import {
  SOULMAP_WS_INACTIVITY_TIMEOUT_MS,
  SOULMAP_WS_RESPONSE_TIMEOUT_MS,
} from '@/utils/soulmap-session-refresh';

const DEFAULT_INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000;
const DEFAULT_RESPONSE_TIMEOUT_MS = 45_000;

export const CONNECTION_LOST_ERROR =
  'The connection dropped before the response finished. Please try again.';

export const RESPONSE_TIMEOUT_ERROR =
  'No response received from the server. Your message may not have been delivered. Please try again.';

export const STREAM_TIMEOUT_ERROR =
  'The stream timed out before the response finished.';

/** The server ended the turn without any reply text (LE-338). */
export const EMPTY_RESPONSE_ERROR = REPLY_FAILED_ERROR;

export interface WebSocketMessage {
  event: string;
  [key: string]: any;
}

export interface SoulMapDocumentQueuedEvent {
  event: 'soul_map_document_queued';
  stream_id: string;
  practice_id: number;
  report_id: number;
  status: 'queued';
  title: 'Full Soul Map';
  document_type: 'soul_map_full';
  delivery_formats: ['pdf'];
  poll_after_seconds: number;
}

/**
 * Why a turn failed. `code` comes from the server's error event (for example
 * `empty_response`), or is `not_sent` when the message never left the client.
 * `postId` is the saved user post a retry should answer.
 */
export interface StreamErrorDetails {
  code?: string;
  postId?: number;
}

export interface StreamingCallbacks {
  onChunk: (content: string) => void;
  onComplete: (content: string) => void;
  onError: (error: string, details?: StreamErrorDetails) => void;
  onStart: () => void;
  onSoulMapDocumentQueued?: (event: SoulMapDocumentQueuedEvent) => void;
  /** The server saved (or reused) the user post this turn answers. */
  onUserPostSaved?: (postId: number) => void;
}

class WebSocketService {
  private ws: WebSocket | null = null;
  private isConnected = false;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 3;
  private reconnectDelay = 1000;
  private messageQueue: WebSocketMessage[] = [];
  private currentStreamingCallbacks: StreamingCallbacks | null = null;
  private accumulatedContent = '';
  private inactivityTimer: number | null = null;
  private longSessionMode = false;

  // Track active practice streams
  private activeStreams: Map<number, string> = new Map();

  // Track last failed message for retry
  private lastFailedMessage: WebSocketMessage | null = null;
  private lastFailedCallbacks: StreamingCallbacks | null = null;
  private retryAttempts = 0;
  private maxRetryAttempts = 2;
  private responseTimeoutTimer: number | null = null;
  // Callers waiting for the open turn to end before starting the next one.
  private streamSettledWaiters: Array<() => void> = [];

  constructor() {
    // Don't auto-connect, wait for explicit connection
  }

  private getWebSocketUrl(): string {
    const baseUrl = getZenApiWsBaseUrl();
    const tenantId = getActiveTenantId();
    const query = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : '';
    const practiceWsUrl = `${baseUrl}/agentos/stream${query}`;
    return practiceWsUrl;
  }

  public async connect(): Promise<boolean> {
    return new Promise((resolve) => {
      try {
        const url = this.getWebSocketUrl();
        console.log('Connecting to WebSocket:', url);

        const socket = new WebSocket(url);
        this.ws = socket;

        this.ws.onopen = () => {
          console.log('WebSocket connected successfully');
          this.isConnected = true;
          this.reconnectAttempts = 0;
          this.startInactivityTimer();

          // Send any queued messages
          while (this.messageQueue.length > 0) {
            const message = this.messageQueue.shift();
            if (message) {
              this.sendMessageInternal(message);
            }
          }

          resolve(true);
        };

        this.ws.onmessage = (event) => {
          console.log('WebSocket message received:', event.data);
          this.handleMessage(event.data);
        };

        this.ws.onclose = (event) => {
          console.log('WebSocket disconnected:', event.code, event.reason);
          // A socket that has already been replaced must not touch the
          // state of the current one.
          if (this.ws !== socket) {
            resolve(false);
            return;
          }
          this.isConnected = false;
          this.clearInactivityTimer();
          // Server-side streams do not survive the socket.
          this.activeStreams.clear();
          this.failActiveStream(CONNECTION_LOST_ERROR);
          this.handleReconnect();
          resolve(false);
        };

        this.ws.onerror = (error) => {
          console.error('WebSocket error:', error);
          this.isConnected = false;
          resolve(false);
        };
      } catch (error) {
        console.error('Failed to create WebSocket:', error);
        resolve(false);
      }
    });
  }

  private handleMessage(data: string): void {
    this.resetInactivityTimer();
    try {
      // Skip non-JSON messages (like "Connected..." status messages)
      if (!data.trim().startsWith('{') && !data.trim().startsWith('[')) {
        console.log('Non-JSON message received:', data);
        return;
      }
      const message = JSON.parse(data);
      console.log('Parsed message:', message);

      switch (message.event) {
        case 'start_ack':
          console.log('Stream started:', message.stream_id);
          this.currentStreamingCallbacks?.onStart();
          this.startResponseTimeout();
          break;

        case 'chunk':
          if (this.currentStreamingCallbacks && message.chunk) {
            this.accumulatedContent += message.chunk;
            console.log('Accumulated content:', this.accumulatedContent);
            this.currentStreamingCallbacks.onChunk(this.accumulatedContent);
            this.startResponseTimeout();
          }
          break;

        case 'user_post_saved':
          if (this.currentStreamingCallbacks && message.post_id != null) {
            this.currentStreamingCallbacks.onUserPostSaved?.(
              Number(message.post_id)
            );
          }
          break;

        case 'soul_map_document_queued':
          if (this.currentStreamingCallbacks) {
            this.currentStreamingCallbacks.onSoulMapDocumentQueued?.(
              message as SoulMapDocumentQueuedEvent
            );
          }
          break;

        case 'end':
          if (this.currentStreamingCallbacks) {
            console.log('Stream ended');
            const callbacks = this.currentStreamingCallbacks;
            const content = this.accumulatedContent;
            this.clearResponseTimeout();
            this.resetStreamingState();
            // LE-338: an `end` with no reply text is a failed turn. Treating
            // it as success marked the message delivered and showed nothing.
            if (content.trim()) {
              callbacks.onComplete(content);
            } else {
              callbacks.onError(EMPTY_RESPONSE_ERROR, {
                code: 'empty_response',
              });
            }
          }
          break;

        case 'terminated':
          console.warn('WebSocket stream terminated:', message.error);
          if (message.stream_id) {
            for (const [practiceId, streamId] of this.activeStreams.entries()) {
              if (streamId === message.stream_id) {
                this.activeStreams.delete(practiceId);
                break;
              }
            }
          }

          if (this.currentStreamingCallbacks) {
            const callbacks = this.currentStreamingCallbacks;
            const content = this.accumulatedContent;
            this.clearResponseTimeout();
            this.resetStreamingState();

            if (content.trim()) {
              callbacks.onComplete(content);
            } else {
              callbacks.onError(
                message.error ||
                  'The connection closed before the response finished. Please try again.'
              );
            }
          }
          break;

        case 'error':
          if (message.stream_id) {
            for (const [practiceId, streamId] of this.activeStreams.entries()) {
              if (streamId === message.stream_id) {
                this.activeStreams.delete(practiceId);
                break;
              }
            }
          } else {
            if (message.error === 'No active stream - call start first') {
              console.log(
                'No active stream error detected, attempting to restart stream'
              );
              this.handleNoActiveStreamError();
              return;
            }
          }
          if (this.currentStreamingCallbacks) {
            const callbacks = this.currentStreamingCallbacks;
            this.clearResponseTimeout();
            this.resetStreamingState();
            callbacks.onError(message.error || 'Unknown error', {
              code: message.code,
              postId:
                message.post_id != null ? Number(message.post_id) : undefined,
            });
          }
          break;

        default:
          console.log('Unknown event:', message.event);
      }
    } catch (error) {
      console.error('Error parsing message:', error);
    }
  }

  private handleInactivity(): void {
    const minutes = Math.round(this.getInactivityTimeoutMs() / 60_000);
    console.warn(`WebSocket inactive for ${minutes} minutes. Disconnecting.`);
    this.disconnect();
  }

  private getInactivityTimeoutMs(): number {
    return this.longSessionMode
      ? SOULMAP_WS_INACTIVITY_TIMEOUT_MS
      : DEFAULT_INACTIVITY_TIMEOUT_MS;
  }

  private getResponseTimeoutMs(): number {
    return this.longSessionMode
      ? SOULMAP_WS_RESPONSE_TIMEOUT_MS
      : DEFAULT_RESPONSE_TIMEOUT_MS;
  }

  private startInactivityTimer(): void {
    this.clearInactivityTimer();
    this.inactivityTimer = window.setTimeout(
      () => this.handleInactivity(),
      this.getInactivityTimeoutMs()
    );
  }

  private resetInactivityTimer(): void {
    this.startInactivityTimer();
  }

  private clearInactivityTimer(): void {
    if (this.inactivityTimer) {
      window.clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }
  }

  private resetStreamingState(): void {
    this.clearResponseTimeout();
    this.currentStreamingCallbacks = null;
    this.accumulatedContent = '';
    // Clear failed message on successful completion
    this.clearFailedMessageState();

    const waiters = this.streamSettledWaiters;
    this.streamSettledWaiters = [];
    waiters.forEach((resolve) => resolve());
  }

  /**
   * Tell the open stream it will not finish, so the chat can check the
   * server for a saved reply instead of waiting for the response timeout.
   */
  private failActiveStream(error: string): void {
    if (!this.currentStreamingCallbacks) return;

    const callbacks = this.currentStreamingCallbacks;
    this.resetStreamingState();
    callbacks.onError(error);
  }

  private startResponseTimeout(): void {
    this.clearResponseTimeout();

    this.responseTimeoutTimer = window.setTimeout(() => {
      if (!this.currentStreamingCallbacks) return;

      console.error('WebSocket response timed out');
      const errorMessage = this.accumulatedContent.trim()
        ? STREAM_TIMEOUT_ERROR
        : RESPONSE_TIMEOUT_ERROR;
      this.currentStreamingCallbacks.onError(errorMessage);
      this.resetStreamingState();
    }, this.getResponseTimeoutMs());
  }

  private clearResponseTimeout(): void {
    if (this.responseTimeoutTimer) {
      window.clearTimeout(this.responseTimeoutTimer);
      this.responseTimeoutTimer = null;
    }
  }

  private clearFailedMessageState(): void {
    this.lastFailedMessage = null;
    this.lastFailedCallbacks = null;
    this.retryAttempts = 0;
  }

  private async handleNoActiveStreamError(): Promise<void> {
    try {
      // Check retry limit
      if (this.retryAttempts >= this.maxRetryAttempts) {
        console.error('Max retry attempts reached for stream restart');
        if (this.lastFailedCallbacks) {
          this.lastFailedCallbacks.onError(
            'Max retry attempts reached - stream failed'
          );
        }
        this.clearFailedMessageState();
        return;
      }

      // Check if connection is still active
      if (!this.isConnectionReady()) {
        console.log('Connection not ready, attempting to reconnect...');
        const reconnected = await this.ensureConnection();
        if (!reconnected) {
          console.error('Failed to reconnect, cannot retry stream');
          this.retryAttempts++;
          return;
        }
      }

      // If we have a last failed message, try to restart the stream
      if (this.lastFailedMessage && this.lastFailedCallbacks) {
        console.log(
          `Retrying last failed message after stream restart (attempt ${this.retryAttempts + 1}/${this.maxRetryAttempts})`
        );

        // Extract practice ID from the failed message
        const practiceId = this.lastFailedMessage.stream?.practice_id;
        const action = this.lastFailedMessage.stream?.action;
        const userMessage = this.lastFailedMessage.stream?.user_message;
        const docId = this.lastFailedMessage.stream?.doc_id;
        const retryPostId = this.lastFailedMessage.stream?.retry_post_id;

        if (practiceId && action && userMessage) {
          // Clear the active stream for this practice to force a new start
          this.activeStreams.delete(practiceId);

          // Increment retry counter
          this.retryAttempts++;

          // Retry the stream (but don't store as failed message again)
          await this.startStream(
            practiceId,
            action,
            userMessage,
            this.lastFailedCallbacks,
            docId,
            false, // Don't store as failed message
            { retryPostId }
          );
        } else {
          console.error('Cannot retry stream - missing required parameters');
          this.clearFailedMessageState();
        }
      } else {
        console.log('No failed message to retry');
      }
    } catch (error) {
      console.error('Error handling no active stream error:', error);
      this.retryAttempts++;
    }
  }

  private handleReconnect(): void {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      console.log(
        `Reconnecting... Attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts}`
      );

      setTimeout(() => {
        this.connect();
      }, this.reconnectDelay * this.reconnectAttempts);
    } else {
      console.error('Max reconnection attempts reached');
    }
  }

  /**
   * Check if the WebSocket connection is truly ready for communication
   */
  private isConnectionReady(): boolean {
    return (
      this.isConnected &&
      this.ws !== null &&
      this.ws.readyState === WebSocket.OPEN
    );
  }

  /**
   * Ensure WebSocket connection is ready, reconnect if needed
   */
  private async ensureConnection(): Promise<boolean> {
    if (this.isConnectionReady()) {
      return true;
    }

    // Reset connection state if we have a stale connection
    if (this.ws && this.ws.readyState !== WebSocket.OPEN) {
      console.log('Detected stale connection, resetting state');
      this.isConnected = false;
    }

    console.log('WebSocket not ready, attempting to connect...');
    return await this.connect();
  }

  /**
   * Send message with automatic retry on connection failure
   */
  private async sendMessageWithRetry(
    message: WebSocketMessage,
    context?: string
  ): Promise<boolean> {
    // First attempt - check if connection is ready
    if (this.isConnectionReady()) {
      if (this.sendMessageInternal(message)) {
        return true;
      }
    }

    // Connection not ready or sending failed, try to reconnect and retry once
    console.log(
      `${context ? context + ': ' : ''}Failed to send message, attempting reconnection...`
    );
    const reconnected = await this.ensureConnection();

    if (reconnected) {
      return this.sendMessageInternal(message);
    }

    return false;
  }

  private sendMessageInternal(message: WebSocketMessage): boolean {
    if (this.isConnectionReady()) {
      this.resetInactivityTimer();
      const messageString = JSON.stringify(message);
      console.log('Sending message:', messageString);
      this.ws!.send(messageString);
      return true;
    }
    return false;
  }

  public async sendMessage(message: WebSocketMessage): Promise<boolean> {
    if (this.isConnectionReady()) {
      return this.sendMessageInternal(message);
    }

    // Queue message and attempt to reconnect
    console.log(
      'WebSocket not connected, queuing message and attempting to reconnect...'
    );
    this.messageQueue.push(message);

    const connected = await this.ensureConnection();
    // Message will be sent automatically when connection is established (in connect method)
    return connected;
  }

  public async startStream(
    practiceId: number,
    action: string,
    userMessage: string,
    callbacks: StreamingCallbacks,
    docId?: number,
    storeAsFailedMessage: boolean = true,
    options?: { retryPostId?: number }
  ): Promise<string> {
    // Ensure connection is ready
    const connected = await this.ensureConnection();
    if (!connected) {
      callbacks.onError('Failed to connect to WebSocket', { code: 'not_sent' });
      return '';
    }

    let messageId = this.activeStreams.get(practiceId);
    const isNewStream = !messageId;

    if (isNewStream) {
      messageId = `practice-${practiceId}-${Date.now()}`;
      this.activeStreams.set(practiceId, messageId);

      // Send start message for new streams
      const startMessage: WebSocketMessage = {
        event: 'start',
        start: {
          stream_id: messageId,
          practice_id: practiceId,
          action,
        },
      };

      console.log('Starting new stream with ID:', messageId);
      const startSent = await this.sendMessageWithRetry(
        startMessage,
        'Start message'
      );
      if (!startSent) {
        callbacks.onError('Failed to send start message after reconnection', {
          code: 'not_sent',
        });
        this.resetStreamingState();
        return '';
      }
    }

    // Set up streaming state
    this.currentStreamingCallbacks = callbacks;
    this.accumulatedContent = '';

    // Send stream message
    const streamMessage: WebSocketMessage = {
      event: 'stream',
      stream: {
        stream_id: messageId,
        practice_id: practiceId,
        action,
        user_message: userMessage,
        doc_id: docId,
        // LE-338: answer this saved post instead of saving the message again.
        ...(options?.retryPostId != null
          ? { retry_post_id: options.retryPostId }
          : {}),
      },
    };

    // Store the message and callbacks for potential retry (only if not a retry attempt)
    if (storeAsFailedMessage) {
      this.lastFailedMessage = streamMessage;
      this.lastFailedCallbacks = callbacks;
    }

    const streamSent = await this.sendMessageWithRetry(
      streamMessage,
      'Stream message'
    );
    if (!streamSent) {
      callbacks.onError('Failed to send stream message after reconnection', {
        code: 'not_sent',
      });
      this.resetStreamingState();
      return '';
    }

    this.startResponseTimeout();

    return messageId!;
  }

  public hasActiveStream(): boolean {
    return this.currentStreamingCallbacks !== null;
  }

  /**
   * Resolves once the open turn has ended, however it ends. Turn events carry
   * no turn id, so a new turn started earlier would receive the old one's
   * remaining chunks and `end`.
   */
  public waitForActiveStreamToSettle(): Promise<void> {
    if (!this.currentStreamingCallbacks) return Promise.resolve();

    return new Promise((resolve) => {
      this.streamSettledWaiters.push(resolve);
    });
  }

  /**
   * A phone that slept or lost signal can come back with the socket already
   * closed but no close event delivered yet, so the open turn waits silently.
   * Fail that turn now so the chat checks for a saved reply or offers Retry.
   * Returns whether the turn was failed.
   */
  public failActiveStreamIfDisconnected(): boolean {
    if (!this.currentStreamingCallbacks) return false;
    if (this.ws && this.ws.readyState === WebSocket.OPEN) return false;

    this.failActiveStream(CONNECTION_LOST_ERROR);
    return true;
  }

  /**
   * Stop delivering this turn's events: the chat settled it another way (it
   * found the saved reply). A late `end` or chunk is then ignored.
   */
  public abandonActiveStream(): void {
    this.resetStreamingState();
  }

  public isWebSocketEnabled(): boolean {
    return import.meta.env.VITE_ENABLE_WEBSOCKET === 'true';
  }

  /** Soul Map sessions can run ~2 hours — use longer WS idle/response timeouts. */
  public setLongSessionMode(enabled: boolean): void {
    this.longSessionMode = enabled;
    if (this.isConnected) {
      this.startInactivityTimer();
    }
  }

  public disconnect(): void {
    console.log('Disconnecting WebSocket');
    this.clearInactivityTimer();
    this.clearResponseTimeout();
    this.resetStreamingState();
    this.messageQueue = [];
    this.activeStreams.clear();
    this.clearFailedMessageState();

    // this.ws?.send(JSON.stringify({ event: 'close' }));

    if (this.ws) {
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.close();
      this.ws = null;
    }

    this.isConnected = false;
    this.reconnectAttempts = 0;
  }

  public getConnectionStatus(): {
    connected: boolean;
    readyState: number | null;
  } {
    return {
      connected: this.isConnected,
      readyState: this.ws?.readyState || null,
    };
  }
}

// Create singleton instance
const websocketService = new WebSocketService();

export default websocketService;
